import { byLastModified } from "utils/sort";
import { matchesStorageType } from "utils/storageProviders";
import { ExtendedPolicy, Item, SecurityOption, PolicyPredicate } from "./types";

import apiRequest from "utils/apiRequestWithErrorCode";

import {
  Authentication,
  SecurityDefinition,
  SecurityPolicy,
  Storage,
  TemplateDefinition,
  TemplatePolicy,
} from "@dashboard-v3/api";
import { State } from "../../context/types";
import { Rule } from "../../../common/types";

export enum AccountManagement {
  MANUAL = "MANUAL",
  AUTO = "AUTO",
  BALANCED = "BALANCED",
}

export enum OptionTypes {
  EMPTY = "EMPTY",
  SEARCH = "SEARCH",
  PRESET = "PRESET",
  DEFINITION = "DEFINITION",
  POLICY = "POLICY",
}

export enum CloudStorageStatus {
  OK = "OK",
  NOT_FOUND = "NOT_FOUND",
  OUT_OF_SYNC = "OUT_OF_SYNC",
}

export const isOrganization = (value: Authentication) =>
  value === "ORGANIZATION";

export const isIndividual = (value: Authentication) => value === "INDIVIDUAL";

export const isSecurityOption = (value: OptionTypes) =>
  value === "PRESET" || value === "DEFINITION" || value === "POLICY";

export const isSearchOption = (value: OptionTypes) => value === "SEARCH";

export const isPresetOption = (value: OptionTypes) => value === "PRESET";

export const canConfirm = (state: State) =>
  !state.steps?.cloudStorage?.done && state.steps?.cloudStorage?.valid;

const emptySecurityOption: SecurityOption = {
  key: "empty",
  type: OptionTypes.EMPTY,
  text: "cloudStorage.security.empty",
};

const searchOption: SecurityOption = {
  key: crypto.randomUUID(),
  type: OptionTypes.SEARCH,
  label: "cloudStorage.security.searchField",
};

type SecurityItems = {
  presets: SecurityDefinition[];
  definitions: SecurityDefinition[];
};

export const toSecurityOptions = (
  definitions: SecurityDefinition[],
  policies: ExtendedPolicy[]
): SecurityOption[] => {
  if (definitions && policies) {
    const definitionItems: SecurityItems = definitions.reduce(
      (acc, item) => {
        if (item.systemDefault) {
          acc.presets.push(item);
        } else {
          acc.definitions.push(item);
        }
        return acc;
      },
      { presets: [], definitions: [] }
    );

    const policyOptions = policies.map(item => ({
      key: item.id,
      type: OptionTypes.POLICY,
      label: "cloudStorage.security.policy.label",
      text: item.name,
      description: item.description,
      value: `${OptionTypes.POLICY}:${item.id}`,
      provider: item.storageType,
      allowFolderCollaboration:
        item.defaultDefinition?.access !== "COLLABORATORS" ||
        !item.predicates.some(p => p.definition?.access === "COLLABORATORS"),
    }));

    const presetOpts = definitionItems.presets.map(item => ({
      key: item.id,
      type: OptionTypes.PRESET,
      access: item.access,
      label: `securityDefinitions:presets.label`,
      text: item.name,
      value: `${OptionTypes.PRESET}:${item.id}`,
      allowFolderCollaboration: item.access !== "COLLABORATORS",
    }));

    const definitionOpts = definitionItems.definitions.map(item => ({
      key: item.id,
      type: OptionTypes.DEFINITION,
      access: item.access,
      label: "securityDefinitions:definitions.label",
      text: item.name,
      value: `${OptionTypes.DEFINITION}:${item.id}`,
      provider: item.allowedStorages[0],
      allowFolderCollaboration: item.access !== "COLLABORATORS",
    }));

    return [
      emptySecurityOption,
      ...presetOpts,
      searchOption,
      ...policyOptions,
      ...definitionOpts,
    ];
  }
  return [];
};

export const toTemplateOptions = (
  definitions: TemplateDefinition[],
  policies: TemplatePolicy[],
  provider: Storage["provider"]
): SecurityOption[] => {
  const policyOpts = policies
    .filter(({ storageType }) => matchesStorageType(storageType, provider))
    .sort(byLastModified)
    .map(({ id, name, description }) => ({
      key: id,
      type: OptionTypes.POLICY,
      label: `cloudStorage.templates.optionLabels.${OptionTypes.POLICY}`,
      text: name,
      value: `${OptionTypes.POLICY}:${id}`,
      description,
    }));

  const definitionOpts = definitions
    .filter(({ storageType }) => matchesStorageType(storageType, provider))
    .sort(byLastModified)
    .map(({ id, name }) => ({
      key: id,
      type: OptionTypes.DEFINITION,
      label: `cloudStorage.templates.optionLabels.${OptionTypes.DEFINITION}`,
      text: name,
      value: `${OptionTypes.DEFINITION}:${id}`,
    }));

  return [...definitionOpts, ...policyOpts];
};

export function isStorageAccountSynced(
  storageAccounts: Item[],
  selectedAccount: Storage
) {
  const item = storageAccounts?.find(
    ({ storageAccount }) => storageAccount.userId === selectedAccount.userId
  );

  return Boolean(item?.option.enabled);
}

export const isAccountNotFound = (items: Item[], user: string) =>
  !items?.some(({ storageAccount }) => storageAccount.userId === user);

// TODO: Remove and use useCloudStorageStatus
export const getCloudStorageStatus = (
  storage: Storage | null,
  availableAccounts: Item[]
) => {
  if (!storage) return CloudStorageStatus.OK;
  if (!availableAccounts) return CloudStorageStatus.OK;

  const { authentication, provider } = storage;
  const isIndividual =
    authentication === "INDIVIDUAL" || provider === "DROPBOX";

  if (isIndividual) {
    const account = availableAccounts?.find(
      ({ storageAccount }) =>
        storageAccount &&
        storageAccount.userId === storage.userId &&
        storageAccount.provider === storage.provider
    );

    if (!account) return CloudStorageStatus.NOT_FOUND;

    if (account && !account.option.enabled) {
      return CloudStorageStatus.OUT_OF_SYNC;
    }
  }

  return CloudStorageStatus.OK;
};

export const asUserValue = (storage: Storage) => {
  const value =
    storage.provider === "DROPBOX" ? storage.asUser : storage.userId;
  return value || "";
};

export const asUserValueCollection = (storage: Storage) => {
  const value =
    storage.provider !== "DROPBOX" ? storage.userIds : storage?.asUsers;
  return value || [];
};

export const canImpersonate = (storage: Storage) => {
  if (storage?.authentication === "ORGANIZATION") return true;
  return storage?.provider === "DROPBOX";
};

export function showSecurityOptionsIn(
  section: "STEP" | "ADVANCED_OPTIONS",
  rule: Rule
) {
  const showInStep =
    rule.actionType === "PROTECT_EMAILS" ||
    rule.actionType === "REPLACE_ATTACHMENTS" ||
    rule.actionType === "ZIP_ATTACHMENTS";

  if (section === "STEP") return showInStep;
  if (section === "ADVANCED_OPTIONS") return !showInStep;

  return false;
}

async function getPolicyDefinition(id: string, defs: SecurityDefinition[]) {
  if (!id) return null;
  const preset = defs.find(s => s.id === id);

  if (preset) return preset;

  const requestPath = `/security-definitions/${id}`;
  const definition = await apiRequest<SecurityDefinition>("GET", requestPath);

  return definition;
}

function populatePredicates(defs: SecurityDefinition[]) {
  return async (predicate: PolicyPredicate) => {
    const { definitionId } = predicate;
    const definition = await getPolicyDefinition(definitionId, defs);

    return { ...predicate, definition };
  };
}

export async function toExtendedPolicies(
  policies: SecurityPolicy[],
  defs: SecurityDefinition[]
) {
  const policyPromises = policies.map(async policy => {
    const { defaultDefinition } = policy;
    const promises = policy.predicates.map(populatePredicates(defs));
    const extendedPredicates = await Promise.all(promises);
    const extendedDefault = await getPolicyDefinition(defaultDefinition, defs);

    return {
      ...policy,
      predicates: extendedPredicates,
      defaultDefinition: extendedDefault,
    };
  });

  return await Promise.all(policyPromises);
}
